import { inject, Injectable } from '@angular/core';
import { GuardResult } from '@angular/router';
import { CustomLoginGuard, OAuthLibWrapperService } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { LoginAuthorizeService } from 'src/app/service/user/login-authorize.service';

/**
 * Mirrors the retry limit used by the OOTB CustomLoginGuard.
 */
const TOTAL_RETRIES = 1;

@Injectable({
  providedIn: 'root',
})
export class ShimanoCustomLoginGuard extends CustomLoginGuard {
  private readonly oAuthLibWrapperService = inject(OAuthLibWrapperService);

  private readonly loginAuthorizeService = inject(LoginAuthorizeService);

  override canActivate(): Observable<GuardResult> {
    if (!this.authConfigService.customLoginEnabled() || !this.windowRef.isBrowser()) {
      // Skip custom-login processing when disabled or during SSR.
      return of(true);
    }

    return this.authService.getCsrfToken().pipe(
      switchMap((token) => {
        // Preserve OOTB behavior.
        this.csrfStateService.set(token);
        this.clearRedirectCount();

        /**
         * reAuthorize() returns:
         *
         * true  = initLoginFlow() was called
         * false = authorize was skipped because it was already triggered
         *         for the current standard/ASM login context
         */
        const authorizeStarted = this.loginAuthorizeService.reAuthorize(() =>
          this.oAuthLibWrapperService.initLoginFlow()
        );

        if (authorizeStarted) {
          /**
           * initLoginFlow() starts a hard browser navigation to /authorize.
           *
           * Stop the current Angular login-route activation so that the
           * LoginComponent does not render briefly before navigation.
           */
          return of(false);
        }

        /**
         * Authorization was already triggered or was not required.
         * Allow the login page to render.
         */
        return of(true);
      }),

      catchError((error) => {
        console.error('[ShimanoCustomLoginGuard] CSRF request failed', error);

        const currentCount = this.getRedirectCount();

        /**
         * OOTB behavior:
         * Stop retrying after the configured retry limit.
         */
        if (currentCount >= TOTAL_RETRIES) {
          this.clearRedirectCount();

          this.showErrorMessage({
            key: 'authMessages.unrecoverableError',
          });

          return this.createRoute('home');
        }

        /**
         * Increment redirect count to prevent an infinite loop.
         */
        this.setRedirectCount(currentCount + 1);

        if (this.federatedLoginService.isLoginDomain && this.federatedLoginService.origin) {
          /**
           * Redirect to the storefront origin login page so that
           * PKCE data is created and stored against the origin.
           */
          const originLoginUrl = this.federatedLoginService.origin + (this.semanticPathService.get('login') ?? '');

          console.warn('[ShimanoCustomLoginGuard] Redirecting to origin login', originLoginUrl);

          this.windowRef.location.href = originLoginUrl;

          // Stop the current Angular navigation.
          return of(false);
        }

        /**
         * Retry by returning the login route.
         */
        return this.createRoute('login');
      })
    );
  }
}
``;
